import { BarChart, Shield } from "react-feather";
import { ColumnDef } from "@tanstack/react-table";
import BigNumber from "bignumber.js";
import { useEffect, useRef, useState } from "react";
import Button from "@axvdex/components/common/Button";
import DashboardPaginationTable from "@axvdex/components/DashboardPaginationTable";
import { OutbidSale, Sale, TopBidder } from "@axvdex/types/solana/outbid";
import useLanguage from "@axvdex/hooks/useLanguage";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { useNicknameMap } from "@axvdex/hooks/solana/useNickname";
import { noNicknamesCheck } from "@axvdex/utils/nicknamesSanitization";
import { ReactComponent as IcnSword } from "@axvdex/assets/icons/icn-sword.svg";
import { NicknameInfo } from "@axvdex/types/solana/nickname";
import { useUserSettings } from "@axvdex/hooks/useUserSettings";
import SaleLeaderboardModal, { IOutbidInfoSaleLeaderboardInput } from "./SonalaSaleLeaderboardModal";
import SaleNicknameTargetModal from "./SolanaSaleNicknameTargetModal";

export const TopBiddersComponent = ({
  topBidders,
  selectedSale,
  selectedOutbidSale,
}: {
  topBidders: TopBidder[];
  selectedSale: Sale;
  selectedOutbidSale: OutbidSale;
}) => {
  const { i18 } = useLanguage();
  const walletInfo = useAppSelector(selectWalletInfo);
  const { userSettings } = useUserSettings();

  // Leaderboard pagination
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  const [saleLeaderboardModalOpen, setSaleLeaderboardModalOpen] = useState<{
    open: boolean;
    outbid: IOutbidInfoSaleLeaderboardInput | null;
    selectedSale: Sale | null;
  }>({
    open: false,
    outbid: null,
    selectedSale: null,
  });

  // Top bidders
  const { nicknames, earlySuccessSetNickname } = useNicknameMap(false);
  const [data, setData] = useState<FormattedTopBidder[]>([]);

  // Animation
  const [prevState, setPrevState] = useState({ topBidders });
  const [prevSelectedSale, setPrevSelectedSale] = useState(selectedSale);
  const app = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPrevState({ topBidders });
    setPrevSelectedSale(selectedSale);
  }, [topBidders, selectedSale, walletInfo, userSettings]);

  useEffect(() => {
    if (selectedSale === prevSelectedSale && JSON.stringify(topBidders) !== JSON.stringify(prevState.topBidders)) {
      const ctx = gsap.context(() => {
        gsap.from(`.topBidders`, 1, {
          ease: "Power3.easeInOut",
          duration: 0.5,
          opacity: 0,
        });
      }, app);
      return () => ctx.revert();
    }
  }, [selectedSale, topBidders]);

  useEffect(() => {
    if (topBidders.length > 0 && walletInfo.isConnected) {
      const formattedData = topBidders.map((bidder, index) => {
        const topBidderNicknameInfo = nicknames.get(bidder.address.toBase58());
        const nickname = `${
          topBidderNicknameInfo?.nickname ? noNicknamesCheck(topBidderNicknameInfo.nickname, userSettings) : "------"
        }`;
        return {
          rank: (index + 1).toString(),
          user: nickname,
          nicknameInfo: topBidderNicknameInfo,
          connected: walletInfo.walletAddress === bidder.address.toBase58(),
          tickets: BigNumber(bidder.totalTicketAmount)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .decimalPlaces(4)
            .toNumber()
            .toLocaleString("en-US"),
          address: bidder.address.toBase58(),
        };
      });
      setData(formattedData);
    }
  }, [topBidders, walletInfo, nicknames, userSettings]);

  const ticketsColumns: ColumnDef<FormattedTopBidder>[] = [
    {
      accessorKey: "rank",
      header: () => <span>{i18("Rank", "sale.details.rank")}</span>,
      cell: ({ row }) => <span style={{ color: "gray" }}>{row.original.rank}</span>,
    },
    {
      id: "message",
      header: () => <span>{i18("User", "sale.details.user")}</span>,
      cell: ({ row }) => (
        <div
          className="inlineFlexbox"
          style={{
            fontWeight: row.original.connected ? "bold" : undefined,
            backgroundImage: row.original.connected
              ? "linear-gradient(92.22deg, #0BF6CC 0.58%, #073DC8 100%)"
              : undefined,
            backgroundClip: row.original.connected ? "text" : undefined,
            WebkitBackgroundClip: row.original.connected ? "text" : undefined,
            color: row.original.connected ? "transparent" : undefined,
          }}
        >
          {row.original.nicknameInfo?.isProtected && <Shield style={{ color: "white", width: "1.5em" }} />}
          {row.original.nicknameInfo?.hasChangedSomeoneElse && <IcnSword style={{ width: "1.5em" }} />}
          {noNicknamesCheck(row.original.user, userSettings)}
          {!row.original.nicknameInfo?.isProtected && !row.original.connected && (
            <SaleNicknameTargetModal
              currentNickname={row.original.user}
              targetAddress={row.original.address}
              earlySuccessSetNickname={earlySuccessSetNickname}
            />
          )}
        </div>
      ),
    },
    {
      accessorKey: "tickets",
      header: () => <span style={{ textAlign: "right" }}>{i18("Tickets", "sale.details.tickets")}</span>,
      cell: ({ row }) => <span style={{ textAlign: "right" }}>{row.original.tickets}</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1em" }}>
        <h3 style={{ marginBottom: "0em" }}>{i18("Top bidders", "sale.details.topBiddersTitle")}</h3>
        <Button
          title={i18("Leaderboard", "sale.leaderboard")}
          btnColor="dark-medium"
          style={{ marginLeft: "1em", color: "white", padding: "1em" }}
          icon={<BarChart />}
          onClick={() =>
            setSaleLeaderboardModalOpen({
              open: true,
              outbid: { project: selectedOutbidSale.name, address: selectedOutbidSale.address },
              selectedSale: selectedSale,
            })
          }
        />
        <SaleLeaderboardModal
          saleLeaderboardModalOpen={saleLeaderboardModalOpen}
          setSaleLeaderboardModalOpen={setSaleLeaderboardModalOpen}
          nicknames={nicknames}
          earlySuccessSetNickname={earlySuccessSetNickname}
        />
      </div>

      <div className="withGradientBorder">
        {0 === topBidders.length ? (
          i18("Top bidders will show here...", "sale.details.topBiddersWillShowHere")
        ) : (
          <DashboardPaginationTable
            tableId="solanaTopBiddersTable"
            extraClassName="dashboardHistoryTable topBidders"
            customData={data}
            customColumns={ticketsColumns}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
      </div>
    </div>
  );
};

type FormattedTopBidder = {
  rank: string;
  user: string;
  nicknameInfo: NicknameInfo;
  connected: boolean;
  tickets: string;
  address: string;
};
